import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";
import {
  Wand2, ArrowRight, ArrowLeft, Check, Loader2, Lock, Palette,
  Type as TypeIcon, Sparkles, Download, Package,
} from "lucide-react";

const STEPS = [
  "idea", "audience", "tone", "logo", "palette", "typography", "slogan", "first_post", "finalize",
];

const TONES = ["casual", "profesional", "formal", "divertido"];

export default function BrandBuilder() {
  const tr = useTr();
  const { user } = useAuth();
  const nav = useNavigate();
  const [eligible, setEligible] = useState(null);
  const [step, setStep] = useState(0);
  const EMPTY_STATE = {
    brand_name: "",
    industry: "",
    audience: "",
    tone: "casual",
    mission: "",
    language: "es-MX",
    chosen_logo_b64: "",
    chosen_palette: [],
    chosen_typography: "",
    chosen_slogan: "",
    chosen_post: "",
  };
  const [state, setState] = useState(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [logoOptions, setLogoOptions] = useState([]);
  const [pkg, setPkg] = useState(null); // palettes/typography/slogans
  const [postResult, setPostResult] = useState(null);
  const [finalBrandId, setFinalBrandId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [hasEcom, setHasEcom] = useState(false);

  const reloadElig = async () => {
    const { data } = await api.get("/brand-builder/eligibility");
    setEligible(data);
    return data;
  };

  const resetWizard = async () => {
    try { await api.delete("/brand-builder/state"); } catch {}
    setState(EMPTY_STATE);
    setStep(0);
    setLogoOptions([]);
    setPkg(null);
    setPostResult(null);
    setFinalBrandId(null);
    setHasEcom(false);
  };

  useEffect(() => {
    reloadElig().then((data) => {
      if (data?.ok) {
        api.get("/brand-builder/state").then(({ data: st }) => {
          // Si el state viejo apunta a la marca que ya quedó entregada (o al brand_id
          // guardado coincide con alguna brand existente), lo ignoramos y empezamos en blanco.
          const orderId = data?.order?.id;
          const stateOrderId = st?.order_id;
          const stale = stateOrderId && orderId && stateOrderId !== orderId;
          if (st && Object.keys(st).length > 0 && !stale) {
            setState((s) => ({ ...s, ...st }));
            if (st.step) setStep(st.step);
          } else if (stale) {
            // Limpia el viejo
            api.delete("/brand-builder/state").catch(() => {});
          }
        });
      }
    });
  }, []);

  const confirmOrder = async () => {
    if (!eligible?.order?.id) return;
    setConfirming(true);
    try {
      await api.post(`/brand-builder/confirm/${eligible.order.id}`);
      toast.success(tr("Briefing confirmado. ¡Empecemos!"));
      // Empezamos wizard en blanco (nueva orden = nueva marca)
      await resetWizard();
      await reloadElig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setConfirming(false);
    }
  };

  const buySku = async (sku) => {
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: sku,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar pago");
    }
  };

  const persist = async (extra = {}) => {
    try {
      await api.post("/brand-builder/state", { ...state, ...extra, step, order_id: eligible?.order?.id });
    } catch {}
  };

  const next = async () => {
    const ns = step + 1;
    setStep(ns);
    await persist({ step: ns });
  };
  const prev = () => setStep(Math.max(0, step - 1));

  // Step: Logo
  const generateLogos = async () => {
    setLoading(true);
    setLogoOptions([]);
    try {
      const { data } = await api.post("/brand-builder/generate-logo", {
        brand_name: state.brand_name,
        industry: state.industry,
        tone: state.tone,
      });
      setLogoOptions(data.logos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar logos");
    } finally {
      setLoading(false);
    }
  };

  // Step: Slogan/palette/typography
  const generatePackage = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/brand-builder/generate-slogan", {
        brand_name: state.brand_name,
        industry: state.industry,
        audience: state.audience,
        tone: state.tone,
        mission: state.mission,
      });
      setPkg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar branding");
    } finally {
      setLoading(false);
    }
  };

  // Step: Primer post
  const generateFirstPost = async () => {
    setLoading(true);
    try {
      // Primero guardar temporal la brand via finalize (o un preview)
      const { data } = await api.post("/generate/post", {
        network: "instagram",
        content_type: "post",
        topic: `presentación de marca ${state.brand_name}: ${state.mission || state.audience}`,
        n_variants: 1,
        extra_instructions: `Tono ${state.tone}. Slogan: ${state.chosen_slogan}`,
        ui_lang: (localStorage.getItem("cia_lang") || "es"),
      });
      setPostResult(data);
      setState((s) => ({ ...s, chosen_post: (data.variants || [""])[0] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar post");
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/brand-builder/finalize", {
        brand_name: state.brand_name,
        industry: state.industry,
        tone: state.tone,
        language: state.language,
        logo_b64: state.chosen_logo_b64,
        palette: state.chosen_palette,
        typography: state.chosen_typography,
        slogan: state.chosen_slogan,
        first_post: state.chosen_post,
        topics: [state.industry].filter(Boolean),
        hero_copy: pkg?.hero_copy || {},
        features: pkg?.features || [],
        about: pkg?.about || "",
      });
      setFinalBrandId(data.brand_id);
      setHasEcom(!!data.has_ecom);
      toast.success(tr("¡Marca creada! Tu kit está listo."));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al finalizar");
    } finally {
      setLoading(false);
    }
  };

  const downloadKit = () => {
    if (!finalBrandId) return;
    const token = localStorage.getItem("cia_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/brand-builder/download/${finalBrandId}`;
    // Use fetch so we can send auth header, then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${state.brand_name.replace(/\s+/g, "_")}_brand_kit.zip`;
        a.click();
      })
      .catch(() => toast.error("Error al descargar"));
  };

  if (eligible === null) return <div className="font-mono-mark text-xs text-[#5C5C5C]">CARGANDO…</div>;

  // Estado 1: sin orden activa → mostrar pricing según el plan del user
  if (!eligible?.order) {
    const pricing = eligible?.pricing || [];
    const isAgency = eligible?.plan === "agency";
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="brand-builder-pricing">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("BRAND BUILDER · IA")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <Wand2 className="w-10 h-10 text-[#E53935]" />
            {tr("Crea tu marca con IA")}
          </h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-4">
            {isAgency
              ? tr("Como Agencia, cada marca nueva tiene un costo one-time. Administras hasta 5 marcas activas por suscripción.")
              : tr("Compra el servicio y obtén tu marca completa: logo, paleta, tipografía, slogan, brand guide PDF y landing web descargable.")}
          </p>
          {isAgency && (
            <div className="mt-3 font-mono-mark text-[10px] text-[#5C5C5C]">
              {tr("MARCAS ACTIVAS")}: <b className="text-[#0A0A0A]">{eligible.brand_count} / {eligible.max_brands}</b>
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {pricing.map((p) => (
            <div key={p.sku} className="bg-white border border-[#0A0A0A] p-6 flex flex-col" data-testid={`sku-${p.sku}`}>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C]">
                {p.sku.includes("ecom") ? tr("INCLUYE E-COMMERCE") : tr("LANDING WEB")}
              </div>
              <div className="font-display text-2xl mt-2">{p.label}</div>
              <div className="font-display text-5xl mt-4">${p.price}<span className="text-sm text-[#5C5C5C] ml-1">USD</span></div>
              <ul className="mt-4 space-y-2 font-mono-data text-sm text-[#5C5C5C] flex-1">
                <li>✓ {tr("Logo IA (3 variantes)")}</li>
                <li>✓ {tr("Paleta, tipografía y slogan")}</li>
                <li>✓ {tr("Brand Guide PDF")}</li>
                <li>✓ {tr("Landing web responsive descargable")}</li>
                {p.sku.includes("ecom") && <li>✓ {tr("Tienda online (catálogo + carrito + checkout)")}</li>}
                <li>✓ {tr("Primer post generado con IA")}</li>
              </ul>
              <button
                onClick={() => buySku(p.sku)}
                className="mt-6 bg-[#E53935] text-white px-5 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal flex items-center justify-center gap-2"
                data-testid={`buy-${p.sku}`}
              >
                {tr("COMPRAR")} · ${p.price} →
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Estado 2: orden paid pero awaiting_brief → confirmación
  if (eligible?.needs_confirmation) {
    const order = eligible.order;
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="brand-builder-confirm">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("BRAND BUILDER · CONFIRMACIÓN")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <Package className="w-10 h-10 text-[#E53935]" />
            {tr("Confirma tu briefing")}
          </h1>
        </div>
        <div className="bg-white border border-[#0A0A0A] p-6 space-y-4">
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("COMPRADO")}</div>
            <div className="font-display text-2xl mt-1">
              {order.sku === "brand_service" && tr("Servicio de Branding + Web")}
              {order.sku === "brand_service_ecom" && tr("Servicio de Branding + Web + E-commerce")}
              {order.sku === "agency_extra_brand" && tr("Marca extra (Agencia)")}
              {order.sku === "agency_extra_brand_ecom" && tr("Marca extra + E-commerce (Agencia)")}
            </div>
            <div className="font-mono-data text-sm text-[#5C5C5C] mt-1">
              ${order.amount} USD · {new Date(order.created_at).toLocaleDateString("es-MX")}
            </div>
          </div>
          <div className="border-t border-[#0A0A0A] pt-4 space-y-2 font-mono-data text-sm">
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("QUÉ INCLUYE TU PAQUETE")}</div>
            <ul className="space-y-1 text-[#0A0A0A]">
              <li>• {tr("Hasta 3 opciones de logo generadas con IA")}</li>
              <li>• {tr("2 paletas de color y 3 combinaciones tipográficas")}</li>
              <li>• {tr("3 slogans alternativos")}</li>
              <li>• {tr("Brand Guide PDF + landing HTML responsive")}</li>
              {order.ecom && <li>• {tr("Tienda online estática con catálogo + carrito + checkout")}</li>}
              <li>• {tr("Un primer post generado con tu nueva marca")}</li>
            </ul>
          </div>
          <div className="bg-[#FFD600] border border-[#0A0A0A] p-4 font-mono-data text-sm">
            {tr("Al confirmar, vas a usar tu crédito de este pago para crear UNA marca. No podrás cambiar después. ¿Estás seguro?")}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => nav("/dashboard")} className="font-mono-mark text-[10px] bg-white border border-[#0A0A0A] px-4 py-3" data-testid="confirm-later">
              {tr("AHORA NO")}
            </button>
            <button
              onClick={confirmOrder}
              disabled={confirming}
              className="font-mono-mark text-[10px] bg-[#E53935] text-white border border-[#E53935] px-4 py-3 hover-brutal disabled:opacity-50"
              data-testid="confirm-order-btn"
            >
              {confirming ? "CONFIRMANDO…" : tr("CONFIRMAR Y EMPEZAR")} →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Estado 3: in_progress → renderizar wizard

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="brand-builder-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("BRAND BUILDER · IA")}</div>
        <div className="flex items-start justify-between flex-wrap gap-4 mt-2">
          <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
            <Wand2 className="w-10 h-10 text-[#E53935]" />
            {tr("Crea tu marca con IA")}
          </h1>
          {!finalBrandId && (
            <button
              onClick={async () => {
                if (window.confirm(tr("¿Empezar de cero? Se borrará el progreso actual."))) {
                  await resetWizard();
                  toast.success(tr("Wizard reiniciado"));
                }
              }}
              className="font-mono-mark text-[10px] bg-white border border-[#0A0A0A] px-3 py-2 hover:bg-[#F4F4F0]"
              data-testid="wizard-reset"
            >
              {tr("EMPEZAR DE CERO")}
            </button>
          )}
        </div>
        <div className="mt-4 flex gap-1 items-center">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-2 flex-1 ${i < step ? "bg-[#E53935]" : i === step ? "bg-[#FFD600]" : "bg-[#E5E5E5]"}`} />
          ))}
          <span className="ml-3 font-mono-mark text-[10px]">{step + 1} / {STEPS.length}</span>
        </div>
      </div>

      <div className="bg-white border border-[#0A0A0A] p-8 min-h-[400px]">
        {step === 0 && (
          <div className="space-y-4" data-testid="step-idea">
            <h2 className="font-display text-3xl">{tr("Paso 1 · Tu idea")}</h2>
            <p className="font-mono-data text-sm text-[#5C5C5C]">{tr("Cuéntanos sobre tu negocio.")}</p>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">NOMBRE DE LA MARCA</span>
              <input required value={state.brand_name} onChange={(e) => setState({ ...state, brand_name: e.target.value })} className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="step-brand-name" />
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("INDUSTRIA / RUBRO")}</span>
              <input value={state.industry} onChange={(e) => setState({ ...state, industry: e.target.value })} placeholder={tr("Ej: café de especialidad, moda sustentable, consultoría…")} className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="step-industry" />
            </label>
            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("MISIÓN / PROPÓSITO (OPCIONAL)")}</span>
              <textarea rows={3} value={state.mission} onChange={(e) => setState({ ...state, mission: e.target.value })} placeholder={tr("¿Qué problema resuelves? ¿Para qué existes?")} className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="step-mission" />
            </label>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="step-audience">
            <h2 className="font-display text-3xl">{tr("Paso 2 · Tu audiencia")}</h2>
            <p className="font-mono-data text-sm text-[#5C5C5C]">{tr("Describe a quién le vendes.")}</p>
            <textarea rows={6} value={state.audience} onChange={(e) => setState({ ...state, audience: e.target.value })} placeholder={tr("Ej: mujeres 25-40, profesionistas, que valoran productos artesanales y comunidad local…")} className="w-full bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm" data-testid="step-audience-input" />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-tone">
            <h2 className="font-display text-3xl">{tr("Paso 3 · Tono de voz")}</h2>
            <p className="font-mono-data text-sm text-[#5C5C5C]">{tr("Así le hablaremos a tu audiencia.")}</p>
            <div className="grid grid-cols-2 gap-3">
              {TONES.map((t) => (
                <button
                  key={t}
                  onClick={() => setState({ ...state, tone: t })}
                  className={`p-5 border-2 text-left ${state.tone === t ? "border-[#E53935] bg-[#FFD600]" : "border-[#0A0A0A] bg-white"}`}
                  data-testid={`step-tone-${t}`}
                >
                  <div className="font-display text-xl">{t.toUpperCase()}</div>
                  <div className="font-mono-data text-xs text-[#5C5C5C] mt-1">
                    {t === "casual" && tr("Amigable, cercano, conversacional")}
                    {t === "profesional" && tr("Experto, confiable, sin jerga")}
                    {t === "formal" && tr("Serio, institucional, preciso")}
                    {t === "divertido" && tr("Juguetón, memero, creativo")}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-logo">
            <h2 className="font-display text-3xl">{tr("Paso 4 · Logo con IA")}</h2>
            <p className="font-mono-data text-sm text-[#5C5C5C]">{tr("Generamos 3 variantes. Elige la que más te guste.")}</p>
            <button onClick={generateLogos} disabled={loading} className="bg-[#0A0A0A] text-white px-5 py-3 font-mono-mark text-xs border border-[#0A0A0A] hover-brutal flex items-center gap-2 disabled:opacity-50" data-testid="generate-logos-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> GENERANDO…</> : <><Sparkles className="w-4 h-4" /> {logoOptions.length ? tr("REGENERAR") : tr("GENERAR LOGOS")}</>}
            </button>
            {logoOptions.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4">
                {logoOptions.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setState({ ...state, chosen_logo_b64: l.b64 })}
                    className={`p-3 border-2 bg-white ${state.chosen_logo_b64 === l.b64 ? "border-[#E53935]" : "border-[#0A0A0A]"}`}
                    data-testid={`logo-option-${l.id}`}
                  >
                    {l.b64 ? (
                      <img src={`data:image/png;base64,${l.b64}`} alt="logo" className="w-full aspect-square object-contain" />
                    ) : (
                      <div className="w-full aspect-square bg-[#F4F4F0] flex items-center justify-center text-xs text-[#5C5C5C]">ERROR</div>
                    )}
                    <div className="font-mono-mark text-[9px] text-center mt-2">{l.variant}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {(step === 4 || step === 5 || step === 6) && (
          <div className="space-y-4" data-testid={`step-${STEPS[step]}`}>
            <h2 className="font-display text-3xl">
              {step === 4 && tr("Paso 5 · Paleta de colores")}
              {step === 5 && tr("Paso 6 · Tipografía")}
              {step === 6 && tr("Paso 7 · Slogan")}
            </h2>
            {!pkg && (
              <button onClick={generatePackage} disabled={loading} className="bg-[#0A0A0A] text-white px-5 py-3 font-mono-mark text-xs border border-[#0A0A0A] hover-brutal flex items-center gap-2 disabled:opacity-50" data-testid="generate-package-btn">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> GENERANDO…</> : <><Sparkles className="w-4 h-4" /> {tr("GENERAR BRANDING")}</>}
              </button>
            )}
            {pkg && step === 4 && (
              <div className="space-y-3">
                {(pkg.palettes || []).map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setState({ ...state, chosen_palette: p.colors })}
                    className={`w-full p-4 border-2 bg-white text-left ${JSON.stringify(state.chosen_palette) === JSON.stringify(p.colors) ? "border-[#E53935]" : "border-[#0A0A0A]"}`}
                    data-testid={`palette-option-${i}`}
                  >
                    <div className="font-display text-lg mb-2 flex items-center gap-2"><Palette className="w-4 h-4" /> {p.name}</div>
                    <div className="flex gap-1">
                      {(p.colors || []).map((c, j) => (
                        <div key={j} className="flex-1 h-12" style={{ backgroundColor: c }} title={c} />
                      ))}
                    </div>
                    <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-2">{(p.colors || []).join(" · ")}</div>
                  </button>
                ))}
              </div>
            )}
            {pkg && step === 5 && (
              <div className="space-y-3">
                {(pkg.typography || []).map((t, i) => {
                  const label = `${t.heading} + ${t.body}`;
                  return (
                    <button
                      key={i}
                      onClick={() => setState({ ...state, chosen_typography: label })}
                      className={`w-full p-4 border-2 bg-white text-left ${state.chosen_typography === label ? "border-[#E53935]" : "border-[#0A0A0A]"}`}
                      data-testid={`typo-option-${i}`}
                    >
                      <div className="font-display text-2xl flex items-center gap-2"><TypeIcon className="w-5 h-5" /> {label}</div>
                      <div className="font-mono-data text-xs text-[#5C5C5C] mt-1">{t.rationale}</div>
                    </button>
                  );
                })}
              </div>
            )}
            {pkg && step === 6 && (
              <div className="space-y-3">
                {(pkg.slogans || []).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setState({ ...state, chosen_slogan: s })}
                    className={`w-full p-4 border-2 bg-white text-left ${state.chosen_slogan === s ? "border-[#E53935]" : "border-[#0A0A0A]"}`}
                    data-testid={`slogan-option-${i}`}
                  >
                    <div className="font-display text-2xl">"{s}"</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 7 && (
          <div className="space-y-4" data-testid="step-first-post">
            <h2 className="font-display text-3xl">{tr("Paso 8 · Primer post")}</h2>
            <p className="font-mono-data text-sm text-[#5C5C5C]">
              {tr("Generemos un post con toda tu marca para que la plataforma aprenda tu voz.")}
            </p>
            <button onClick={generateFirstPost} disabled={loading} className="bg-[#0A0A0A] text-white px-5 py-3 font-mono-mark text-xs border border-[#0A0A0A] hover-brutal flex items-center gap-2 disabled:opacity-50" data-testid="generate-first-post-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> GENERANDO…</> : <><Sparkles className="w-4 h-4" /> {postResult ? tr("REGENERAR") : tr("GENERAR PRIMER POST")}</>}
            </button>
            {state.chosen_post && (
              <div className="bg-[#F4F4F0] border border-[#0A0A0A] p-5 font-mono-data text-sm whitespace-pre-wrap" data-testid="first-post-preview">
                {state.chosen_post}
              </div>
            )}
          </div>
        )}

        {step === 8 && (
          <div className="space-y-4 text-center" data-testid="step-finalize">
            <h2 className="font-display text-3xl">{tr("¡Última parada!")}</h2>
            {!finalBrandId ? (
              <>
                <p className="font-mono-data text-sm text-[#5C5C5C]">
                  {tr("Tu marca está lista. Vamos a crearla y empaquetar todos los archivos para descarga.")}
                </p>
                <button onClick={finalize} disabled={loading} className="bg-[#E53935] text-white px-6 py-4 font-mono-mark text-xs border border-[#E53935] hover-brutal flex items-center justify-center gap-2 mx-auto disabled:opacity-50" data-testid="finalize-btn">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> CREANDO…</> : <><Package className="w-4 h-4" /> {tr("CREAR MARCA Y GENERAR PAQUETE")}</>}
                </button>
              </>
            ) : (
              <>
                <Check className="w-14 h-14 mx-auto text-[#E53935]" />
                <div className="font-display text-4xl">{tr("¡Tu marca está lista!")}</div>
                <p className="font-mono-data text-sm text-[#5C5C5C]">
                  {hasEcom
                    ? tr("Descarga tu kit completo: logo, brand guide PDF, paleta, tipografía, slogan, landing HTML y tienda e-commerce.")
                    : tr("Descarga tu kit completo: logo, brand guide PDF, paleta, tipografía, slogan y landing HTML.")}
                </p>
                {hasEcom && (
                  <div className="bg-[#FFD600] border border-[#0A0A0A] p-3 font-mono-data text-sm inline-block">
                    ✨ {tr("INCLUYE TIENDA ONLINE")}
                  </div>
                )}
                <div className="flex gap-3 justify-center flex-wrap">
                  <button onClick={downloadKit} className="bg-[#0A0A0A] text-white px-6 py-4 font-mono-mark text-xs border border-[#0A0A0A] hover-brutal flex items-center gap-2" data-testid="download-kit-btn">
                    <Download className="w-4 h-4" /> {tr("DESCARGAR KIT (ZIP)")}
                  </button>
                  <button onClick={() => nav("/dashboard")} className="bg-white px-6 py-4 font-mono-mark text-xs border border-[#0A0A0A]">
                    {tr("IR AL DASHBOARD")} →
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Nav buttons */}
      {!finalBrandId && (
        <div className="flex justify-between items-center">
          <button onClick={prev} disabled={step === 0} className="font-mono-mark text-[10px] px-4 py-2 bg-white border border-[#0A0A0A] flex items-center gap-1 disabled:opacity-30" data-testid="wizard-prev">
            <ArrowLeft className="w-3 h-3" /> ATRÁS
          </button>
          <button
            onClick={next}
            disabled={
              (step === 0 && !state.brand_name.trim()) ||
              (step === 3 && !state.chosen_logo_b64) ||
              (step === 4 && !state.chosen_palette.length) ||
              (step === 5 && !state.chosen_typography) ||
              (step === 6 && !state.chosen_slogan) ||
              step === STEPS.length - 1
            }
            className="font-mono-mark text-[10px] px-6 py-3 bg-[#E53935] text-white border border-[#E53935] flex items-center gap-1 disabled:opacity-30"
            data-testid="wizard-next"
          >
            CONTINUAR <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
